import { useCallback, useMemo } from "react";
import { matchPath, useHistory, useLocation, useParams } from "react-router-dom";

import { searchTo } from "./params";
import { composeRouteUrl } from "./routes";
import { IParamsActions } from "./types";

export interface IUseRouterProps<P, S> {
  pathname: string;
  state?: any;
  matchParams: P;
  searchParams: S;
  push: (path: string, state?: any) => void;
  replace: (path: string, state?: any) => void;
  pushSearchParams: (searchObj: any) => void;
  replaceSearchParams: (searchObj: any) => void;
  goBack: () => void;
  isActive: (path: string) => boolean;
  isActiveExact: (path: string) => boolean;
}

export const useRouter = <P, S>({ ensureMatchParams, ensureSearchParams }: IParamsActions<P, S> = {}): IUseRouterProps<
  P,
  S
> => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const push = useCallback(
    (path: string, state?: any) => {
      history.push(path, state);
    },
    [history],
  );

  const replace = useCallback(
    (path: string, state?: any) => {
      history.replace(path, state);
    },
    [history],
  );

  const pushSearchParams = useCallback(
    (searchObj: any) => {
      history.push(composeRouteUrl(location.pathname, {}, searchObj));
    },
    [history, location.pathname],
  );

  const replaceSearchParams = useCallback(
    (searchObj: any) => {
      history.replace(composeRouteUrl(location.pathname, {}, searchObj));
    },
    [history, location.pathname],
  );

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const isActive = useCallback(
    (path: string) => {
      return !!matchPath(location.pathname, { path, strict: false, exact: false });
    },
    [location.pathname],
  );

  const isActiveExact = useCallback(
    (path: string) => {
      return !!matchPath(location.pathname, { path, strict: false, exact: true });
    },
    [location.pathname],
  );

  const searchParams = useMemo(() => {
    const params = searchTo(location.search);
    return ensureSearchParams ? ensureSearchParams(params) : params;
  }, [location.search, ensureSearchParams]) as S;

  const matchParams = useMemo(() => {
    return ensureMatchParams ? ensureMatchParams(params) : params;
  }, [params, ensureMatchParams]) as P;

  return {
    pathname: location.pathname,
    state: location.state,
    push,
    replace,
    pushSearchParams,
    replaceSearchParams,
    goBack,
    isActive,
    isActiveExact,
    matchParams,
    searchParams,
  };
};
